import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import {
  confirmAccountRecovery,
  isAuthenticated,
  requestAccountRecovery,
  signIn,
} from '../../services/cognitoService';
import './Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect') || searchParams.get('returnUrl');
  const returnTo = (location.state as { from?: string })?.from || redirectParam || '/';

  // Form state (matching mobile structure)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  // Recovery state for soft-deleted accounts
  const [recoveryStep, setRecoveryStep] = useState<'none' | 'prompt' | 'code' | 'success'>('none');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryEmailMasked, setRecoveryEmailMasked] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(returnTo);
    }
  }, [navigate, returnTo]);

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('theychat-remembered-email');
    if (rememberedEmail) {
      setFormData((prev) => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true,
      }));
    }
  }, []);

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.emailRequired');
    } else if (!isValidEmail(formData.email)) {
      errors.email = t('validation.emailInvalid');
    }

    if (!formData.password) {
      errors.password = t('validation.passwordRequired');
    } else if (formData.password.length < 8) {
      errors.password = t('validation.passwordMinLength');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Parse and format error messages for better UX
  const formatErrorMessage = (message: string): { title: string; description: string } => {
    if (message.includes('not confirmed') || message.includes('not verified')) {
      return {
        title: t('auth.login.errors.notVerified', 'Email Not Verified'),
        description: t(
          'auth.login.errors.notVerifiedDesc',
          'Please check your email and click the verification link before logging in.',
        ),
      };
    }
    if (message.includes('Incorrect') || message.includes('incorrect')) {
      return {
        title: t('auth.login.errors.incorrectCredentials', 'Invalid Credentials'),
        description: t(
          'auth.login.errors.incorrectCredentialsDesc',
          'The email or password you entered is incorrect. Please try again.',
        ),
      };
    }
    if (message.includes('User does not exist') || message.includes('user not found')) {
      return {
        title: t('auth.login.errors.userNotFound', 'Account Not Found'),
        description: t(
          'auth.login.errors.userNotFoundDesc',
          'No account found with this email. Please check your email or create a new account.',
        ),
      };
    }
    if (message.includes('too many') || message.includes('limit exceeded')) {
      return {
        title: t('auth.login.errors.tooManyAttempts', 'Too Many Attempts'),
        description: t(
          'auth.login.errors.tooManyAttemptsDesc',
          'You have made too many login attempts. Please wait a few minutes and try again.',
        ),
      };
    }
    if (message.includes('network') || message.includes('Network')) {
      return {
        title: t('auth.login.errors.network', 'Connection Error'),
        description: t(
          'auth.login.errors.networkDesc',
          'Please check your internet connection and try again.',
        ),
      };
    }
    return {
      title: t('auth.login.errors.loginFailed', 'Login Failed'),
      description: message,
    };
  };

  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await signIn({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
      });

      if (result.success) {
        // Handle remember me
        if (formData.rememberMe) {
          localStorage.setItem('theychat-remembered-email', formData.email.toLowerCase().trim());
        } else {
          localStorage.removeItem('theychat-remembered-email');
        }

        setSuccess(t('auth.login.loginSuccess', 'Login successful! Redirecting...'));
        setTimeout(() => {
          navigate(returnTo);
        }, 1000);
      } else if (result.message === 'USER_DISABLED') {
        // Account was soft-deleted — offer recovery
        setRecoveryEmail(formData.email.toLowerCase().trim());
        setRecoveryStep('prompt');
        setRecoveryError('');
      } else {
        setError(result.message);
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('auth.login.loginFailed', 'Login failed. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign In
  const handleGoogleSignIn = () => {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);

    if (cognitoDomain && clientId) {
      // Store return URL for after OAuth callback
      sessionStorage.setItem('theychat-auth-return-url', returnTo);
      window.location.href = `${cognitoDomain}/oauth2/authorize?identity_provider=Google&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=openid+email+profile&prompt=login`;
    } else {
      setError(t('auth.login.googleNotConfigured', 'Google sign-in is not configured'));
    }
  };

  // Handle Apple Sign In
  const handleAppleSignIn = () => {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);

    if (cognitoDomain && clientId) {
      sessionStorage.setItem('theychat-auth-return-url', returnTo);
      window.location.href = `${cognitoDomain}/oauth2/authorize?identity_provider=SignInWithApple&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=openid+email+profile`;
    } else {
      setError(t('auth.login.appleNotConfigured', 'Apple sign-in is not configured'));
    }
  };

  // Handle recovery request (Step 1: send code)
  const handleRecoveryRequest = async () => {
    setRecoveryLoading(true);
    setRecoveryError('');
    try {
      const result = await requestAccountRecovery(recoveryEmail);
      setRecoveryEmailMasked(result.emailMasked || recoveryEmail);

      setRecoveryStep('code');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('30 day')) {
        setRecoveryError(
          t(
            'auth.login.recovery.periodExpired',
            'Your recovery period has expired. The account can no longer be recovered.',
          ),
        );
      } else {
        setRecoveryError(
          message || t('auth.login.recovery.recoverFailed', 'Recovery failed. Please try again.'),
        );
      }
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Handle recovery confirmation (Step 2: verify code)
  const handleRecoveryConfirm = async () => {
    if (recoveryCode.length !== 6) return;
    setRecoveryLoading(true);
    setRecoveryError('');
    try {
      await confirmAccountRecovery(recoveryEmail, recoveryCode);
      setRecoveryStep('success');
    } catch (err: unknown) {
      setRecoveryError(
        err instanceof Error
          ? err.message
          : t('auth.login.recovery.recoverFailed', 'Recovery failed. Please try again.'),
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  // Reset recovery state and go back to login
  const handleRecoveryDismiss = () => {
    setRecoveryStep('none');
    setRecoveryEmail('');
    setRecoveryCode('');
    setRecoveryError('');
    setRecoveryEmailMasked('');
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  return (
    <div className="login-page">
      {/* Background - same as Landing Page */}
      <div className="bg-accent-shape" />

      <Header />

      <main className="login-main">
        {/* Hero Section */}
        <section className="login-hero">
          <div className="login-hero-content">
            <div className="hero-line"></div>
            <h2 className="hero-label">{t('auth.login.heroLabel', 'Login')}</h2>
            <div className="hero-line"></div>
          </div>
          <h1 className="hero-title">{t('auth.login.heroTitle', 'Welcome to TheyChat')}</h1>
        </section>

        {/* Login Form Card */}
        <section className="login-card">
          {/* Icon Badge */}
          <div className="login-icon-badge">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <title>Chat Icon</title>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>

          {/* Don't have account link */}
          <div className="login-signup-link">
            <span>{t('auth.login.noAccount')}</span>
            <Link to="/register">{t('auth.login.signUp')}</Link>
          </div>

          {/* Form Content */}
          <div className="login-form-content">
            <div className="form-header">
              <h3>
                {recoveryStep !== 'none'
                  ? t('auth.login.recovery.accountDeactivated', 'Account Deactivated')
                  : t('auth.login.title')}
              </h3>
              <p>
                {recoveryStep !== 'none'
                  ? ''
                  : t('auth.login.subtitle', 'Please enter your account information to login')}
              </p>
            </div>

            {/* Account Recovery Flow */}
            {recoveryStep === 'prompt' && (
              <div className="recovery-section">
                <div className="alert alert-warning">
                  <span className="alert-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>Warning Icon</title>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  <div className="alert-content">
                    <p className="alert-title">
                      {t(
                        'auth.login.recovery.accountDeactivated',
                        'Your account has been deactivated',
                      )}
                    </p>
                    <p className="alert-message">
                      {t(
                        'auth.login.recovery.recoverPrompt',
                        'Would you like to recover your account? Your data is still available for recovery.',
                      )}
                    </p>
                  </div>
                </div>
                {recoveryError && (
                  <div className="alert alert-error">
                    <span className="alert-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Error Icon</title>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </span>
                    <div className="alert-content">
                      <p className="alert-message">{recoveryError}</p>
                    </div>
                  </div>
                )}
                <div className="recovery-actions">
                  <button
                    type="button"
                    className="btn-login"
                    onClick={handleRecoveryRequest}
                    disabled={recoveryLoading}
                  >
                    {recoveryLoading
                      ? t('auth.login.recovery.recovering', 'Recovering...')
                      : t('auth.login.recovery.recoverButton', 'Recover Account')}
                  </button>
                  <button
                    type="button"
                    className="btn-recovery-cancel"
                    onClick={handleRecoveryDismiss}
                    disabled={recoveryLoading}
                  >
                    {t('auth.login.recovery.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            {recoveryStep === 'code' && (
              <div className="recovery-section">
                <p className="recovery-instruction">
                  {t(
                    'auth.login.recovery.enterCode',
                    'Enter the 6-digit recovery code sent to {{email}}',
                    { email: recoveryEmailMasked },
                  )}
                </p>
                {recoveryError && (
                  <div className="alert alert-error">
                    <span className="alert-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Error Icon</title>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </span>
                    <div className="alert-content">
                      <p className="alert-message">{recoveryError}</p>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="recoveryCode">
                    {t('auth.login.recovery.codeLabel', 'Recovery Code')}
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="recoveryCode"
                      value={recoveryCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setRecoveryCode(val);
                        if (recoveryError) setRecoveryError('');
                      }}
                      placeholder={t('auth.login.recovery.codePlaceholder', '000000')}
                      maxLength={6}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      disabled={recoveryLoading}
                    />
                  </div>
                </div>
                <div className="recovery-actions">
                  <button
                    type="button"
                    className="btn-login"
                    onClick={handleRecoveryConfirm}
                    disabled={recoveryLoading || recoveryCode.length !== 6}
                  >
                    {recoveryLoading
                      ? t('auth.login.recovery.recovering', 'Recovering...')
                      : t('auth.login.recovery.recoverButton', 'Recover Account')}
                  </button>
                  <button
                    type="button"
                    className="btn-recovery-cancel"
                    onClick={handleRecoveryDismiss}
                    disabled={recoveryLoading}
                  >
                    {t('auth.login.recovery.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            {recoveryStep === 'success' && (
              <div className="recovery-section">
                <div className="alert alert-success">
                  <span className="alert-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <title>Success Icon</title>
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </span>
                  <span>
                    {t(
                      'auth.login.recovery.recoverSuccess',
                      'Account recovered successfully! You can now log in with your existing credentials.',
                    )}
                  </span>
                </div>
                <div className="recovery-actions">
                  <button type="button" className="btn-login" onClick={handleRecoveryDismiss}>
                    {t('auth.login.recovery.backToLogin', 'Back to Login')}
                  </button>
                </div>
              </div>
            )}

            {/* Regular login flow - hide when recovery is active */}
            {recoveryStep !== 'none' ? null : (
              <>
                {error &&
                  (() => {
                    const { title, description } = formatErrorMessage(error);
                    return (
                      <div className="alert alert-error">
                        <span className="alert-icon">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Error Icon</title>
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                        </span>
                        <div className="alert-content">
                          <p className="alert-title">{title}</p>
                          <p className="alert-message">{description}</p>
                        </div>
                      </div>
                    );
                  })()}

                {success && (
                  <div className="alert alert-success">
                    <span className="alert-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Success Icon</title>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </span>
                    <span>{success}</span>
                  </div>
                )}

                <form onSubmit={handleLogin} className="login-form">
                  {/* Email Input */}
                  <div className="form-group">
                    <label htmlFor="email">{t('auth.login.emailLabel')}</label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={validationErrors.email ? 'error' : ''}
                        placeholder={t('auth.login.emailPlaceholder')}
                        disabled={loading}
                        autoComplete="email"
                      />
                    </div>
                    {validationErrors.email && (
                      <span className="error-message">{validationErrors.email}</span>
                    )}
                  </div>

                  {/* Password Input */}
                  <div className="form-group">
                    <label htmlFor="password">{t('auth.login.passwordLabel')}</label>
                    <div className="input-wrapper password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={validationErrors.password ? 'error' : ''}
                        placeholder={t('auth.login.passwordPlaceholder')}
                        disabled={loading}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <title>Show Password</title>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                          >
                            <title>Hide Password</title>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {validationErrors.password && (
                      <span className="error-message">{validationErrors.password}</span>
                    )}
                  </div>

                  {/* Remember Me & Forgot Password Row */}
                  <div className="login-options-row">
                    <label className="remember-me-label">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <span className="checkbox-custom"></span>
                      <span>{t('auth.login.rememberMe')}</span>
                    </label>
                    <Link to="/forgot-password" className="forgot-password-link">
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </div>

                  {/* Terms Text */}
                  <div className="terms-text">
                    <span>
                      {t('auth.login.termsPrefix')}
                      <Link to="/terms">{t('auth.login.termsOfUse')}</Link>
                      {t('auth.login.and')}
                      <Link to="/privacy">{t('auth.login.privacyPolicy')}</Link>
                    </span>
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button type="submit" className="btn-login" disabled={loading}>
                      {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
                    </button>
                  </div>
                </form>

                {/* Divider */}
                <div className="auth-divider">
                  <span>{t('common.orContinueWith', 'or continue with')}</span>
                </div>

                {/* Google Sign In */}
                <button
                  type="button"
                  className="btn-google"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  id="login-google-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <title>Google Icon</title>
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>{t('auth.login.continueWithGoogle', 'Continue with Google')}</span>
                </button>

                {/* Apple Sign In */}
                <button
                  type="button"
                  className="btn-apple"
                  onClick={handleAppleSignIn}
                  disabled={loading}
                  id="login-apple-btn"
                >
                  <svg
                    width="18"
                    height="22"
                    viewBox="0 0 814 1000"
                    aria-hidden="true"
                    fill="white"
                  >
                    <title>Apple Icon</title>
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-42.3-150.3-107.9C27.8 746.6 0 634.6 0 529.2c0-170.3 111-260.3 220-260.3 56.5 0 103.4 37.3 138.8 37.3 33.7 0 87.3-39.5 151.7-39.5 24.4 0 108.2 2.6 168 75.5zm-126.5-185c-7.1 32.8-21.4 65.7-43.6 92.5-28.8 35.2-69.3 62.8-108.2 57.5 0-2.6-.6-5.2-.6-7.8 0-34.5 14.3-70.3 36.5-95.2 21.4-24.8 61.6-43.9 95.4-52.8 1.3.1 2.6.1 3.9.1.1 2.5.8 5.1.6 5.7z" />
                  </svg>
                  <span>{t('auth.login.continueWithApple', 'Continue with Apple')}</span>
                </button>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
